// @ts-strict-ignore
import React, {
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AggregationEditorContext,
  AggregationsSubEditorContainer,
} from 'flow/editor/components/aggregations';
import { RuleBuilderContext } from '../../context';
import { ThresholdContext } from './context';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { resetSubEditor } from 'flow/store/redux/thunks';
import { getAggregationConfigForAttribute } from 'flow/store/engine/update/helpers';
import type { AggregationSourceData } from 'flow/editor/components/aggregations/types';
import {
  aggregationComponentsToSourceData,
  sourceDataToAggregationComponents,
} from 'flow/editor/components/aggregations/transformers';
import { getIsSaveDisabled } from 'flow/editor/components/aggregations/helpers';
import { selectRuleBuilderActiveSubEditor, selectWorkflowProps } from 'flow/store/redux/selectors';
import { RuleBuilderSubEditorSide } from 'flow/store/engine/types/utils';
import { getIsPreview } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/helpers';
import { getMultiSelectProps } from './helpers';
import { updateThreshold as updateThresholdSlice } from 'flow/store/redux/slice';
import { keyBinds } from '../../constants';
import { datadogRum } from '@datadog/browser-rum';
import { useKeyboardFocus } from '@usealloy/component-library';

const AggregationsWrapper = () => {
  const dispatch = useAppDispatch();
  const { populatedThreshold } = useContext(ThresholdContext);
  const { editMode, keyboardFocus } = useContext(RuleBuilderContext);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const { aggregationConfig: aggregationConfigByObjectType } = useAppSelector(selectWorkflowProps);

  const side = activeSubEditor.side === RuleBuilderSubEditorSide.LHS ? 'left' : 'right';
  const attribute = populatedThreshold[side]?.attribute;

  const aggregationConfig = useMemo(
    () => getAggregationConfigForAttribute(attribute, aggregationConfigByObjectType),
    [attribute, aggregationConfigByObjectType],
  );

  const multiSelectProps = useMemo(() => getMultiSelectProps({
    action: populatedThreshold[side].action,
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  }), [populatedThreshold, side, editMode, activeSubEditor.thresholdId]);

  const aggregationEditorContainerRef = useRef<HTMLDivElement>(null);
  const [aggregationContextSourceData, setAggregationContextSourceData] = useState<
  AggregationSourceData
  >(() => aggregationComponentsToSourceData(
    populatedThreshold[side].aggregationComponents,
  ));

  const isSaveDisabled = useMemo(
    () => getIsSaveDisabled(aggregationContextSourceData),
    [aggregationContextSourceData],
  );

  const aggregationKeyboardFocus = useKeyboardFocus({
    containerRef: aggregationEditorContainerRef,
    keyBinds,
    onError: (error, context) => datadogRum.addError(error, context),
  });

  const aggregationEditorContext = useMemo(() => {
    const handleClose = () => {
      dispatch(resetSubEditor());
      keyboardFocus.enable();
    };

    return {
      sourceData: aggregationContextSourceData,
      setSourceData: setAggregationContextSourceData,
      handleSave: () => {
        const existingComponents = populatedThreshold[side].aggregationComponents;
        // objectSlug/objectVersionPublicVersion may be absent when aggregationConfig
        // wasn't loaded yet at threshold-creation time. Fall back to the current config,
        // but let any already-persisted values take precedence via the spread.
        const withFallbacks = [{
          objectSlug: aggregationConfig?.objectType,
          objectVersionPublicVersion: aggregationConfig?.publicVersion,
          ...existingComponents[0],
        }, ...existingComponents.slice(1)];
        dispatch(updateThresholdSlice({
          path: `${populatedThreshold.publicId}.${side}.aggregationComponents`,
          value: sourceDataToAggregationComponents(
            aggregationContextSourceData,
            withFallbacks,
          ),
        }));
        handleClose();
      },
      handleCancel: () => {
        setAggregationContextSourceData(aggregationComponentsToSourceData(
          populatedThreshold[side].aggregationComponents,
        ));
        handleClose();
      },
      aggregationEditorContainerRef,
      multiSelectProps,
      aggregationConfig,
      isSaveDisabled,
      validationErrors: {},
      keyboardFocus: aggregationKeyboardFocus,
    };
  }, [
    dispatch,
    keyboardFocus,
    aggregationContextSourceData,
    populatedThreshold,
    side,
    multiSelectProps,
    aggregationConfig,
    isSaveDisabled,
    aggregationKeyboardFocus,
  ]);

  if (!aggregationConfig) return null;

  return (
    <AggregationEditorContext.Provider value={aggregationEditorContext}>
      <AggregationsSubEditorContainer />
    </AggregationEditorContext.Provider>
  );
};

export default AggregationsWrapper;
